<template>
    <div :class="css.container">
        <div :class="css.aminos" v-if="mode == 'aminos'">
            <div :class="css.name" class="color-1 font-bold font-variable">
                {{ $t("Residues", { name: origin.symbol }, 2) }}（{{ data.aminos.length }}）
            </div>
            <ui-table2 :class="css.body" :data="data.aminos" :option="option" :error="$t('NoDataAvailable')">
                <template #id="{ data }">
                    <div :class="css.ids">
                        <template v-if="!data.pmids?.length">--</template>
                        <template v-else>
                            <!-- * 数据渲染 -->
                            <template v-if="data.visible">
                                <render-pmids v-for="value in data.pmids" :data="value" />
                            </template>
                            <template v-else>
                                <render-pmids v-for="value in data.pmids.slice(0, 4)" :data="value" />
                            </template>

                            <!-- * 更多控制器 -->
                            <div v-if="data.pmids.length > 5" :class="css.module">
                                <div :class="css_normalize.assets" @click="data.visible = !data.visible">
                                    {{ data.visible ? $t("Less") : $t("More") }}
                                </div>
                            </div>
                        </template>
                    </div>
                </template>
            </ui-table2>
        </div>

        <div :class="css.references" v-if="mode == 'references'">
            <div :class="css.name" class="color-1 font-bold font-variable">{{ $t("References") }}（{{ data.references.length }}）</div>
            <div :class="css.body">
                <div :class="css.reference" v-for="(value, index) in data.references">
                    <div :class="css.header">
                        <ProjectRoute :class="css.tag" :to="`https://pubmed.ncbi.nlm.nih.gov/${value.id}/`">
                            <div :class="css.number">#{{ (index + 1).toString().padStart(2, "0") }}</div>
                            <div :class="css.id">PMID：{{ value.id }}</div>
                        </ProjectRoute>

                        <div :class="css.info">
                            <div :class="css.type">{{ $t("Species") }}：</div>
                            <div :class="css.content" class="font-bold color-1">{{ value.species }}</div>
                        </div>

                        <div :class="css.info">
                            <div :class="css.type">{{ $t("Tissue") }}：</div>
                            <div :class="css.content" class="font-bold color-1">{{ value.tissue }}</div>
                        </div>

                        <div :class="css.info">
                            <div :class="css.type">{{ $t("ResiduesSequence") }}：</div>
                            <div :class="css.content">
                                <template v-if="!value.sequences?.length">--</template>
                                <ui-tooltip v-else :class="css_normalize.assets" :class-extra-name="css.sequences" direction="bottom">
                                    <template #default>{{ value.sequences.length }}</template>
                                    <template #content>
                                        <div :class="css.sequence" v-for="(sequence, index) in value.sequences">
                                            <div :class="css.number" class="color-1 font-bold">#{{ (index + 1).toString().padStart(2, "0") }}：</div>
                                            <div :class="css.content">{{ sequence }}</div>
                                        </div>
                                    </template>
                                </ui-tooltip>
                            </div>
                        </div>

                        <div :class="css.info">
                            <div :class="css.type">{{ $t("Cells") }}：</div>
                            <div :class="css.content" class="font-bold color-1 ellipsis">{{ value.cells }}</div>
                        </div>
                    </div>

                    <div :class="css.content">
                        <div :class="css.reference_name" class="color-1 font-bold">{{ $t("Title") }}：</div>
                        <div :class="css.reference_content">
                            <div>
                                <span>{{ value.name }}</span>
                                <span :class="[css.more, css_normalize.assets]" @click="value.visible = !value.visible">
                                    {{ value.visible ? $t("Less") : $t("Description", 2) }}
                                </span>
                            </div>

                            <Transition v-on="ons.animation">
                                <div v-if="value.visible">
                                    <div :class="css.division"></div>
                                    <div>{{ value.descript }}</div>
                                </div>
                            </Transition>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="tsx" setup>
//* 组件库
import { UiTable2, UiTooltip } from "various-ui";
//* 公共组件
import ProjectRoute from "@/components/common/ProjectRoute.vue";

//* 组件属性
import type { ModulePMID } from "../../index";
import { ModulePropsOption } from "./index";
import { useComposable } from "./src/composable";
//* 样式
import css from "./style/index.module.less";
import css_normalize from "@/assets/style/src/index.module.less";

//* 组件属性注册
defineProps(ModulePropsOption);

//* 组合函数
const { ons, option } = useComposable();

//* 渲染函数
const renderPmids = defineComponent({
    props: { data: { type: Object as PropType<ModulePMID>, required: true } },
    components: { UiTooltip, ProjectRoute },
    render: function () {
        const renderContent = () => {
            return (
                <div>
                    <div class={css.id}>
                        <span class="color-1 font-bold">PMID:</span>
                        <span class={css.content}>{this.data.id}</span>
                    </div>
                    <div class={css.division}></div>
                    <div class={css.name}>{this.data.name}</div>
                    <div class={css.descript}>{this.data.descript}</div>
                </div>
            );
        };

        const renderDefault = () => {
            return (
                <project-route class={css_normalize.assets} to={`https://pubmed.ncbi.nlm.nih.gov/${this.data.id}/`}>
                    {this.data.id}
                </project-route>
            );
        };

        return (
            <div class={css.module}>
                <ui-tooltip direction="bottom" class-extra-name={css.view}>
                    {{ default: () => renderDefault(), content: () => renderContent() }}
                </ui-tooltip>
            </div>
        );
    },
});
</script>
